import { DataTableService } from 'src/app/shared/data-table/data-table.service';
import { cgpAlertTypes } from './../../../../shared/cgp-alert-dialog/cgp-alert-dialog.service';
import { Component, OnInit, Input, OnDestroy, ViewChild } from '@angular/core';
import { LazyLoadEvent } from 'primeng/api/primeng-api';
import { ColumnDef, PageOptions } from 'src/app/shared/data-table/data-table.interface';
import { EcmToolChangeService } from './ecm-tool-change.service';
import { BehaviorSubject } from 'rxjs';
import { filter, finalize } from 'rxjs/operators';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { EtcMultiRowUpdateComponent } from './etc-multi-row-update/etc-multi-row-update.component';
import { DataTableComponent } from 'src/app/shared/data-table/data-table.component';
import { EcmService } from '../ecm.service';
import { UserService } from '../../../../../modules/user.service';
import { EtcAuthoritiesComponent } from './etc-authorities/etc-authorities.component';
import { EtcEditToolChangeComponent } from './etc-edit-tool-change/etc-edit-tool-change.component';
import { ToastrService } from 'ngx-toastr';
import { CgpConfirmationDialogService } from '../../../../shared/cgp-confirmation-dialog/cgp-confirmation-dialog.service';
import { CgpAlertDialogService } from '../../../../shared/cgp-alert-dialog/cgp-alert-dialog.service';
import { DataTableValidators } from '../../../../shared/data-table/validators';
import { Router } from '@angular/router';

interface EcmToolChange {
   toolId: number;
   toolChangeId: number;
   partChangeId: number;
   partNumber: string;
   oppositePart: string;
   partType: string;
   releaseLevel: string;
   necLec: string;
   toolAffected: number;
   certDataAffected: number;
   tlLeadRelLvl: string;
   dtShippedToDatabank: string;
   necCompleteDate: string;
   designSourceId: number;
   programName: string;
   pdi: string;
   lhStablePartCode: string;
   rhStablePartCode: string;
   authNumber: string;
   authNumberN: string;
   authTypeName: string;
   addedBy: number;
   fmtLastUpdatedDate: string;
   approvalDate: string;
   fmtdtShippedToDatabank: string;
   fmtCompleteDate: string;
   fmtNecCompleteDate: string;
   fmtReleaseLevel: string;
}

const DROP_DOWN_SORT_FIELD = {
   'designSourceId': 'sourceName'
};

@Component({
   // tslint:disable-next-line: component-selector
   selector: 'ecm-tool-change',
   templateUrl: './ecm-tool-change.component.html',
   styleUrls: ['./ecm-tool-change.component.scss']
})
export class EcmToolChangeComponent implements OnInit, OnDestroy {

   pageOptions: PageOptions = this.dataTableService.pageOptionsDefaultSort('necLec');
   usedNecLec: any[];
   fieldSelected: any;
   readonly NEC_LEC_REGEX = /^[A-Za-z][A-Za-z]?$/;
   private designSources: any[] = null;
   data: EcmToolChange[];
   loading = false;
   readonly defaultSize = 10;
   private readonly defaultOrder = 'asc';
   private readonly defaultSortField = 'partNumber';
   total = 0;
   toolId: number;
   selectedToolId: number;
   isLeadPart = false;
   columns: ColumnDef[] = [
      { field: 'partNumber', name: 'Lead Part #', editable: false, width: '5.5em', tooltip: 'Read Only' },
      { field: 'oppositePart', name: 'Opp Part #', editable: false, width: '5.5em', tooltip: 'Opposite Part Number - Read Only' },
      { field: 'partType', name: 'File Type', editable: false, width: '5.5em', tooltip: 'Read Only' },
      { field: 'releaseLevel', name: 'Rel Lvl', editable: false, width: '3.5em', tooltip: 'Release level - Read Only' },
      {
         field: 'necLec', name: 'NEC LEC', width: '4em', editable: false, validators: [DataTableValidators.maxLength(7), {
            validator: (data: string) => data === 'REL' || data === '(REL)' || data === 'rel' || data === '(rel)' || data === '' || this.NEC_LEC_REGEX.test(data),
            snippit: 'The only valid entries for the NEC LEC are REL, (REL) or 2 letters (i.e. A, AA, B, BA, etc...)'
         }],
         tooltip: 'Notification Engineering Change LEC - Required before entering the Shipped to Databank Date',
         cellEditable: (col: ColumnDef, data: any) => !data.dtShippedToDatabank
      },
      {
         field: 'toolAffected', name: 'TL Affected?', type: 'checkbox', width: '4em', editable: false,
         tooltip: 'Check this box to identify the Tool as being affected - Required before entering the Shipped to Databank Date',
         cellEditable: (col: ColumnDef, data: any) => !data.dtShippedToDatabank
      },
      {
         field: 'certDataAffected', name: 'Cert Data?', type: 'checkbox', width: '4em', editable: false,
         tooltip: 'Check this box to identify there is Certification Data - Required before entering the Shipped to Databank Date',
         cellEditable: (col: ColumnDef, data: any) => !data.dtShippedToDatabank
      },
      {
         field: 'tlLeadRelLvl', name: 'TL Lead Rel Lvl', width: '4em', editable: false, validators: DataTableValidators.maxLength(8),
         tooltip: 'Tool Lead Release Level - Required before entering the Shipped to Databank Date',
         cellEditable: (col: ColumnDef, data: any) => !data.dtShippedToDatabank
      },
      {
         field: 'dtShippedToDatabank', name: 'Shipped to Databank', type: 'date', width: '6.5em', editable: false,
         tooltip: 'Shipped to Databank Date - Can be entered after entering TL Lead Rel Lvl and Design Source',
         cellEditable: (col: ColumnDef, data: any) => data.tlLeadRelLvl && data.designSourceId
      },
      {
         field: 'necCompleteDate', name: 'NEC Compl Date', type: 'date', width: '6em', editable: false,
         tooltip: 'Notification Engineer Change Complete Date - Can be entered after entering the Shipped to Databank Date',
         cellEditable: (col: ColumnDef, data: any) => data.dtShippedToDatabank
      },
      {
         field: 'designSourceId', name: 'Design Source', type: 'dropdown', editable: false, optionsListField: 'id', optionsList: this.designSources, optionsListName: 'sourceName', width: '11em',
         tooltip: 'Design Source - Required before entering the Shipped to Databank Date', placeHolderField: 'sourceName', sortingDisabled: false
      },
      { field: 'authNumber', name: 'Auth. # (alpha)', width: '5.5em', editable: false, tooltip: 'Authority Number (Alpha) - Read Only' },
      { field: 'authNumberN', name: 'Auth. # (numeric)', width: '5.5em', editable: false, tooltip: 'Authority Number (numeric) - Read Only' },
      { field: 'authTypeName', name: 'Auth. Type', width: '4em', editable: false, tooltip: 'Authority Type - Read Only' },
      { field: 'programName', name: 'Program Name', width: '12em', editable: false, tooltip: 'Read Only' },
      { field: 'pdi', name: 'PDI', width: '3em', editable: false, tooltip: 'Read Only' },
      { field: 'lhStablePartCode', name: 'LH CSPC', width: '4em', editable: false, tooltip: 'Left Hand Common Stable Part Code - Read Only' },
      { field: 'rhStablePartCode', name: 'RH CSPC', width: '4em', editable: false, tooltip: 'Right Hand Common Stable Part Code - Read Only' },
   ];

   @Input() resourced = false;
   @Input() selectedEcmTool$: BehaviorSubject<any>;

   @ViewChild('toolChangeTable', { static: true }) toolChangeTable: DataTableComponent;

   constructor(
      private readonly router: Router,
      private readonly ecmToolChangeService: EcmToolChangeService,
      private readonly ecmService: EcmService,
      private readonly dialog: MatDialog,
      private readonly toastr: ToastrService,
      private readonly userService: UserService,
      private readonly confirmation: CgpConfirmationDialogService,
      private readonly cgpAlertDialogService: CgpAlertDialogService,
      private readonly dataTableService: DataTableService
   ) {
   }

   get hasSelectedNone(): boolean {
      return (this.toolChangeTable.selections || [])
         .filter((row) => Object.keys(row).length !== 0).length === 0;
   }

   get hasSelectedMultiple(): boolean {
      return (this.toolChangeTable.selections || [])
         .filter((row) => Object.keys(row).length !== 0).length >= 2;
   }
   get hasSelectedSingle(): boolean {
      return (this.toolChangeTable.selections || [])
         .filter((row) => Object.keys(row).length !== 0).length === 1;
   }

   get hasSelectedSingleCheck(): boolean {
      return (this.toolChangeTable.selections || []).length === 1;
   }

   get hasShippedToDatabank(): boolean {
      return (this.toolChangeTable.selections || [])
         .some((row) => row.dtShippedToDatabank !== null && row.dtShippedToDatabank !== '');
   }


   isPartNumberSelected(event: any) {
      if (event) {
         this.isLeadPart = event.partNumber ? true : false;
      }
   }

   ngOnInit() {

      this.columns.forEach(val => {
         if (val.field === 'toolAffected' || val.field === 'certDataAffected'
            || val.field === 'dtShippedToDatabank' || val.field === 'necCompleteDate' || val.field === 'designSourceId') {
            return val.headerHighlight = false;
         }

         if (val.field === 'necLec' || val.field === 'tlLeadRelLvl') {
            return (val.type = 'readonly');
         }
      });

      if (this.router.url === '/tprecm/ecm/re-source') {
         this.resourced = true;
         this.columns.forEach(val => { val.editable = false; val.headerHighlight = false; });
      }

      if (this.resourced) {
         this.columns.forEach(val => {
            if (val.field === 'partNumber' || val.field === 'oppositePart' || val.field === 'partType' || val.field === 'releaseLevel'
               || val.field === 'necLec' || val.field === 'tlLeadRelLvl' || val.field === 'authNumber' || val.field === 'authNumberN'
               || val.field === 'authTypeName' || val.field === 'programName' || val.field === 'pdi' || val.field === 'lhStablePartCode' || val.field === 'rhStablePartCode') {
               return (val.type = 'readonly');
            }
         });
      }

      this.selectedEcmTool$.subscribe(
         (selectedEcmTool) => {
            this.clearSelections();
            if (selectedEcmTool) {
               this.selectedToolId = selectedEcmTool.toolId;
               const toolId = selectedEcmTool.toolId;
               this.updateSelectedEcmTool(toolId);
               this.updateDesignSources();
            } else {

               this.data = [];
            }
         }
      );
   }

   ngOnDestroy() {
      if (this.selectedEcmTool$) { this.selectedEcmTool$.unsubscribe(); }
   }

   selectAllFilterMessage(event: any) {
      const filteredSelections = event;
      if (filteredSelections.length !== this.data.length) {
         const messageString = 'You cannot check the Tool Change with the Shipped to Databank Date. You can click on "Clear All" to unselect all the selections.';
         this.cgpAlertDialogService.showAlertDialog({
            message: messageString.replace(/\\n/g, '\n'),
            alert: cgpAlertTypes.success,
            closeLabel: 'Close'
         });
      }
   }

   onMultiRowUpdateClick() {
      this.dialog.open(EtcMultiRowUpdateComponent,
         {
            disableClose: true,
            autoFocus: true,

            data: {
               selected: this.toolChangeTable.selections[0],
               selections: this.toolChangeTable.selections,
               designSources: this.designSources
            }
         }).afterClosed().subscribe((val) => {
            if (val && val.reload) { this.updateSelectedEcmTool(this.toolId, val.isMultiedit, null, null); }
         });

   }

   refreshTools(necLec = false) {
      if (!necLec) {
         this.pageOptions.sort = 'toolChangeId';
         this.pageOptions.order = 'desc';
      }

      this.updateSelectedEcmTool(this.toolId);
   }

   onLazyLoad(event: LazyLoadEvent) {
      const sortField: string = DROP_DOWN_SORT_FIELD[event.sortField] || event.sortField;
      
      this.pageOptions.order = event.sortOrder === 1 ? 'asc' : 'desc';
      this.pageOptions.size = event.rows;
      this.pageOptions.sort = sortField;
      this.pageOptions.page = event.first / this.pageOptions.size;
      this.updateSelectedEcmTool(this.toolId);
   }

   onAddToolChangeClick() {
      this.dialog.open(EtcEditToolChangeComponent,
         {
            disableClose: true,
            autoFocus: true,
            data: {
               isNew: true,
               toolId: this.toolId,
               selections: this.toolChangeTable.selections,
               usedNecLec: this.usedNecLec,
               designSources: this.designSources
            }
         }).afterClosed().subscribe((val) => {
            if (val && val.reload) { this.refreshTools(true); }
         });
      this.clearSelections();
   }

   onEditToolChangeClick() {
      this.dialog.open(EtcEditToolChangeComponent,
         {
            disableClose: true,
            autoFocus: true,
            data: {
               isNew: false,
               selected: this.toolChangeTable.selections[0],
               selections: this.toolChangeTable.selections,
               usedNecLec: this.usedNecLec,
               designSources: this.designSources
            }
         }).afterClosed().subscribe((val) => {
            if (val && val.reload) {
               this.updateSelectedEcmTool(this.toolId, null, val.toolChangeId, null);
            }
         });
   }

   onEditAuthoritiesClick() {

      this.dialog.open(EtcAuthoritiesComponent,
         {
            disableClose: true,
            width: '74vw',
            height: '74vh',
            autoFocus: true,
            data: {
               selected: this.toolChangeTable.selections[0],
               selections: this.toolChangeTable.selections,
            }
         }).afterClosed().subscribe((val) => {
            if (val && val.reload) { this.updateSelectedEcmTool(this.toolId, null, val.toolChangeId, null); }
         });
   }

   updateDesignSources() {
      this.userService.login().subscribe();
      this.ecmService.getDesignSourceCriteria()
         .subscribe((val) => {
            this.designSources = val;
            this.columns
               .find((column: ColumnDef) => column.field === 'designSourceId')
               .optionsList = this.designSources;
         }
         );
   }

   clearSelections() {
      this.toolChangeTable.selections = [];
   }

   updateSelectedEcmTool(toolId, isMultiedit?: boolean, toolChangeId?: number, isdeleted?: boolean) {
      if (!toolId) {
         return;
      }

      this.toolId = toolId;
      this.loading = true;
      this.ecmToolChangeService.getToolChange(toolId, this.pageOptions)
         .pipe(
            filter(Boolean),
            finalize(() => {
               this.loading = false;
               if (toolChangeId) {
                  this.toolChangeTable.selections = [this.data.find(tool => tool.toolChangeId === toolChangeId)];
               }
               if (isMultiedit || isdeleted) {
                  this.clearSelections();
               }
            })
         ).subscribe(
            (res: { totalCount: number, data: any[] }) => {
               this.total = res ? res.totalCount : 0;
               this.data = res ? res.data : [];
               this.usedNecLec = res.data.filter(val => val.dtShippedToDatabank !== null).map(val => val.necLec);
               this.data.filter(val => val.releaseLevel === null)
                  .forEach(row => row.releaseLevel = 'N/R');
            });
   }

   updateToolChange(event: any) {
      this.updateSelectedEcmTool(event.data.toolId);
      const body = event.data;
      if (body.necLec === ('REL' || '(REL' || 'REL)')) { body.necLec = '(REL)'; }
      if (body.necLec === ('rel' || '(rel' || 'rel)')) { body.necLec = '(REL)'; }
      else { body.necLec = body.necLec.toUpperCase(); }

      if (event.field === 'necLec' && !body.dtShippedToDatabank) {
         this.usedNecLec.forEach(val => {
            if (body.necLec === val) {
               const ref = this.cgpAlertDialogService.showAlertDialog({
                  message: 'Nec "' + body.necLec.toUpperCase() + '" has been databanked and can no longer be used. Try again.'
               });

               ref.afterClosed()
                  .subscribe(() => {
                     body.sourceName = null;
                     body.necCompleteDate = null;
                     body.necLec = null;
                     this.ecmToolChangeService.updateToolChange(body)
                        .subscribe();
                  });
               return;
            }
         });
      }


      if (!body.dtShippedToDatabank && body.necCompleteDate) {
         const ref = this.cgpAlertDialogService.showAlertDialog({
            message: 'Removing the Shipped to Databank Date will also remove the NEC Complete Date'
         });

         ref.afterClosed()
            .subscribe(() => {
               body.sourceName = null;
               body.necCompleteDate = null;
               this.ecmToolChangeService.updateToolChange(body)
                  .subscribe();
            });
         return;
      }

      body.sourceName = undefined;
      this.ecmToolChangeService.updateToolChange(body)
         .subscribe();
   }


   onDeleteToolChangeClick() {
      if (this.toolChangeTable.selections.length === 0) {
         this.toastr.error('Please select a tool change to delete');
         return;
      }
      if (this.toolChangeTable.selections.length > 1) {
         this.toastr.error('Only 1 tool change can be deleted at one time!');
         return;
      } else {
         this.confirmation.showConfirmationDialog({
            title: 'Delete Tool Change',
            message: `Are you sure you want to delete this Tool Change?`,
            alert: 'danger',
            onYesClick: () => {
               const isdeleted = true;
               this.ecmToolChangeService.deleteToolChange(this.toolChangeTable.selected.toolId, this.toolChangeTable.selected.toolChangeId)
                  .subscribe(() => this.updateSelectedEcmTool(this.toolChangeTable.selected.toolId, null, null, isdeleted));

               this.confirmation.closeConfirmationDialog();
            },
            onNoClick: () => this.confirmation.closeConfirmationDialog()
         });
      }
   }
}
